import { useState } from 'react';
import { updateFolderNote, ScrapbookFolder } from '@/api';
import { useNotification } from '@/components/general/NotificationProvider';
import XLight from '@/assets/xLight.svg';

interface FolderNoteProps {
  currentFolder: ScrapbookFolder;
  onNoteUpdate: (note: string | null) => void;
}

export default function FolderNote({ currentFolder, onNoteUpdate }: FolderNoteProps) {
  const { showNotification } = useNotification();
  const [show, setShow] = useState(false);
  const [editableNote, setEditableNote] = useState('');

  const formCancelled = () => {
    setEditableNote('');
    setShow(false);
  };

  const formOked = async () => {
    setShow(false);
    await saveNote();
  };

  const saveNote = async () => {
    await sendNoteUpdateToServer(editableNote);
  };

  const deleteNote = async () => {
    await sendNoteUpdateToServer(null);
  };

  const sendNoteUpdateToServer = async (newNote: string | null) => {
    try {
      const result = await updateFolderNote(currentFolder.id, newNote);

      if (result.succeeded) {
        setEditableNote(newNote || '');
        onNoteUpdate(newNote);
      } else {
        showNotification('Failed to update folder note: ' + result.message, 'error');
      }
    } catch {
      showNotification('Failed to update folder note', 'error');
    }

    setEditableNote('');
    setShow(false);
  };

  const editNote = () => {
    setEditableNote(currentFolder.note || '');
    setShow(true);
  };

  return (
    <div className="folder-note-editor border border-gray-300 p-2.5">
      <div className="note-display flex items-center">
        <p className="m-0 flex-1" dangerouslySetInnerHTML={{ __html: currentFolder.note || '' }} />
        <button onClick={editNote} className="edit-button ml-2.5 cursor-pointer px-2.5 py-1.5 text-xs">
          Edit
        </button>
        <button onClick={deleteNote} className="delete-button ml-2.5 cursor-pointer px-2.5 py-1.5 text-xs">
          Delete
        </button>
      </div>

      {show && (
        <div className="theme--dark fixed inset-0 z-[201] bg-[#212121]/46">
          <div className="absolute top-1/5 left-1/2 h-[238px] w-full max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between p-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Folder Note</h3>
              <XLight className="h-6 w-6 cursor-pointer hover:bg-gray-100" onClick={formCancelled} />
            </div>
            <div className="p-4">
              <div className="flex items-start gap-3">
                <textarea
                  value={editableNote}
                  onChange={(e) => setEditableNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && formOked()}
                  className="h-24 min-h-24 w-80 flex-1 resize-none rounded border border-black px-3 py-2"
                  autoFocus
                />
                <button
                  onClick={formOked}
                  className="mt-6 cursor-pointer rounded-full bg-gray-500 p-2 text-white hover:bg-gray-600"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
